<template>
  <div class="offcanvas-body">
    <div class="row">
      <div class="col-12">
        <TextInput
          required
          label="Nome completo"
          :model="nameModel"
          type="text"
          placeholder="Qual seu nome?"
        />
      </div>
      <div class="col-12">
        <TextInput
          required
          label="CPF"
          :model="documentNumberModel"
          type="text"
          :mask="'###.###.###-##'"
          placeholder="Qual seu CPF?"
        />
      </div>
      <div class="col-12 col-md-6">
        <SelectInput
          required
          @selectChanged="countryCodeChanged"
          :model="countryCodeModel"
          :options="phoneCodes"
          label="DDI"
          propertyForValue="dial_code"
          propertyForText="label"
        />
      </div>
      <div class="col-12 col-md-6">
        <TextInput
          required
          label="Telefone (Whatsapp)"
          :model="phoneModel"
          type="text"
          :mask="phoneMask"
          placeholder="Qual seu telefone?"
        />
      </div>
      <div class="col-12 text-center">
        <span class="text-danger text-center">{{ errorMessage }}</span>
      </div>
    </div>
    <FormButton
      class="w-100 mt-auto"
      label="Concluir"
      :disabled="!meta.valid"
      @onClick="handleAccountUpdate"
      :marginVertical="false"
      :loading="submitting"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import type IPhoneCode from "@/interfaces/IPhoneCode";
import { useVeeField } from "@/composables/useVeeField";
import { buildAccountUpdateSchema } from "@/schemas/account";
import { useSessionStore } from "@/store/useSessionStore";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import PhoneCodes from "@/utils/PhoneCodes";
import { goBackCanvas } from "@/views/OffcanvasViews/OffcanvasViewsConfig";
import { useUpdateUser } from "@/composables/useAuth";

const sessionStore = useSessionStore();
const session = computed(() => sessionStore.session);

const phoneCodes = PhoneCodes;
const phoneCode = ref<IPhoneCode>(PhoneCodes[0]);
const phoneMask = ref("(##) #####-####");
const submitting = ref(false);
const errorMessage = ref("");

const { handleSubmit, meta } = useForm({
  validationSchema: computed(() =>
    toTypedSchema(buildAccountUpdateSchema(phoneCode.value?.mask.length)),
  ),
  initialValues: {
    name: session.value?.name ?? "",
    document_number: session.value?.document_number ?? "",
    country_code: session.value?.country_code ?? "",
    phone: session.value?.phone ?? "",
  },
});

const nameModel = useVeeField("name");
const documentNumberModel = useVeeField("document_number");
const countryCodeModel = useVeeField("country_code");
const phoneModel = useVeeField("phone");

const { mutateAsync: updateUser } = useUpdateUser();

const handleAccountUpdate = handleSubmit(async (values) => {
  try {
    submitting.value = true;
    const params = {
      ...values,
      phone: (values.phone as any).clear(),
      document_number: (values.document_number as any).clear(),
    };
    await updateUser({ id: session.value!.id, ...params });
    EventBus.$emit(EventBusNames.SHOW_TOAST, {
      title: "Informações alteradas",
      msg: "Suas informações foram alteradas com sucesso",
    });
    goBackCanvas();
  } catch (error: any) {
    console.log(error);
    errorMessage.value = error.message;
  } finally {
    submitting.value = false;
  }
});

function countryCodeChanged(value: string) {
  phoneModel.$model = "";
  const pc = phoneCodes.find((e) => e.dial_code === value);
  if (pc) {
    phoneCode.value = pc;
    phoneMask.value = pc.mask || "##########";
  }
}
</script>
